import { useEffect, useMemo, useSyncExternalStore } from "react";

import { getInitialValue, type InitialValue } from "./initialValue.ts";
import type { NodeTransientState } from "./types.ts";

interface NodeTransientStateStore<T, I> {
  // Number of components using this store. When it reaches 0, the store is removed.
  count: number;
  // Subscribe to changes in the store (i.e. calls to set). Returns a function to unsubscribe.
  subscribe: (callback: () => void) => () => void;
  get: () => T | I;
  set: (newValue: T) => void;
}

const createStore = <T, I>(initialValue: InitialValue<I>): NodeTransientStateStore<T, I> => {
  let value: T | I = getInitialValue(initialValue);

  const callbacks = new Set<() => void>();

  const subscribe = (callback: () => void) => {
    callbacks.add(callback);

    return () => {
      callbacks.delete(callback);
    };
  };

  const get = () => value;

  const set = (newValue: T) => {
    value = newValue;
    for (const callback of callbacks) {
      callback();
    }
  };

  return { count: 0, subscribe, get, set };
};

/**
 * Exported only for tests, there's no valid use in real components outside NodeTransientStateProvider.
 * Creates a new, empty NodeTransientState instance.
 * @internal
 */
export const createNodeTransientState = (): NodeTransientState => {
  const active = new Map<number, NodeTransientStateStore<unknown, unknown>>();

  return {
    useTransientState: <T, I>(id: number, initialValue: InitialValue<I>): [T | I, (newValue: T) => void] => {
      const store = useMemo(() => {
        let result = active.get(id) as NodeTransientStateStore<T, I>;
        if (!result) {
          result = createStore<T, I>(initialValue);
          active.set(id, result as NodeTransientStateStore<unknown, unknown>);
        }
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- don't update on initialValue changes
      }, [id]);

      // Keep track of how many components are using this store, so we can clean it up when they're all gone.
      useEffect(() => {
        if (active.get(id) !== store) {
          // This seems broken, but it seems to happen on HMR.
          // Maybe there's some import.meta.hot magic to catch specifically that.
          console.warn("useTransientState() called on store that was removed (HMR?).");
          active.set(id, store as NodeTransientStateStore<unknown, unknown>);
        }

        store.count++;
        return () => {
          if (--store.count === 0) {
            active.delete(id);
          }
        };
      }, [id, store]);

      // Subscribe to the store to update the local value.
      const value = useSyncExternalStore(store.subscribe, store.get);
      return [value, store.set];
    },
  };
};
